import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import "./profile.css";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useUser, { isUserDoctor, hasRole, getAuthorized, putAuthorized, postAuthorized } from "../../../hooks/User";
import { UserSessionContext } from "../../../contexts/UserSessionContext";
import { useTutorial } from "../../../contexts/TutorialContext";
import { ROUTES } from "../../../routes";
import { useContext } from "react";
import MedicalDocumentsSection from "./MedicalDocumentsSection";

// Error message component
function ErrorMessage({ message, onDismiss }) {
    return (
        <div className="profile-error" role="alert">
            <div className="profile-error-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
            <div className="profile-error-content">
                <p className="profile-error-text">{message}</p>
            </div>
            {onDismiss && (
                <button className="profile-error-close" onClick={onDismiss} aria-label="Закрыть">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                    </svg>
                </button>
            )}
        </div>
    );
}

// Inline editable field component
function EditableField({ 
    label, 
    currentValue, 
    placeholder = "Не указано",
    onSave, 
    onRequestUnlink = null,
    type = "text",
    validate = null
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [newValue, setNewValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const handleEdit = () => {
        setNewValue(currentValue || "");
        setIsEditing(true);
        setError("");
    };

    const handleCancel = () => {
        setIsEditing(false);
        setNewValue("");
        setError("");
    };

    const handleSave = async () => {
        if (validate) {
            const validationError = validate(newValue);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        if (newValue === currentValue) {
            setIsEditing(false);
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            await onSave(newValue);
            setIsEditing(false);
        } catch (err) {
            setError(err.message || "Ошибка сохранения");
        } finally {
            setIsLoading(false);
        }
    };

    const displayValue = currentValue || placeholder;
    const hasValue = !!currentValue;

    return (
        <div className="editable-field">
            <div className="field-header">
                <span className="field-label">{label}</span>
                {!isEditing && (
                    <div className="field-actions-row">
                        {hasValue && onRequestUnlink && (
                            <button className="field-unlink-btn" onClick={onRequestUnlink} aria-label="Отвязать" disabled={isLoading}>
                                <span className="btn-text">Отвязать</span>
                                <span className="btn-icon" aria-hidden="true">✕</span>
                            </button>
                        )}
                        <button className="field-edit-btn" onClick={handleEdit} aria-label="Изменить">
                            <span className="btn-text">Изменить</span>
                            <span className="btn-icon" aria-hidden="true">✎</span>
                        </button>
                    </div>
                )}
            </div>

            {!isEditing ? (
                <div className={`field-value ${!hasValue ? "field-empty" : ""}`}>
                    {displayValue}
                </div>
            ) : (
                <div className="field-editor">
                    <input
                        type={type}
                        value={newValue}
                        onChange={(e) => {
                            setNewValue(e.target.value);
                            setError("");
                        }}
                        placeholder={`Введите ${label.toLowerCase()}`}
                        className={error ? "input-error" : ""}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                            if (e.key === "Escape") handleCancel();
                        }}
                    />
                    {error && <ErrorMessage message={error} onDismiss={() => setError("")} />}
                    <div className="field-actions">
                        <button 
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Отмена
                        </button>
                        <button 
                            className="btn-save"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading ? "Сохранение..." : "Сохранить"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Doctor code activation component (for patients only)
function DoctorActivation({ onActivate, isLoading }) {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!code.trim()) {
            setError("Введите код доступа");
            return;
        }

        setError("");
        try {
            await onActivate(code.trim());
            setCode("");
            setIsExpanded(false);
        } catch (err) {
            setError(err.message || "Неверный код доступа");
        }
    };

    const handleCancel = () => {
        setIsExpanded(false);
        setCode("");
        setError("");
    };

    if (!isExpanded) {
        return (
            <div className="doctor-activation-collapsed">
                <span className="activation-hint">Есть код доступа врача?</span>
                <button 
                    className="btn-activate"
                    onClick={() => setIsExpanded(true)}
                >
                    Стать врачом
                </button>
            </div>
        );
    }

    return (
        <form className="doctor-activation-form" onSubmit={handleSubmit}>
            <div className="activation-input-wrapper">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                        setCode(e.target.value);
                        setError("");
                    }}
                    placeholder="Введите код доступа"
                    className={error ? "input-error" : ""}
                    autoFocus
                />
                {error && <span className="field-error">{error}</span>}
            </div>
            <div className="activation-actions">
                <button 
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancel}
                    disabled={isLoading}
                >
                    Отмена
                </button>
                <button 
                    type="submit" 
                    className="btn-save"
                    disabled={isLoading}
                >
                    {isLoading ? "Активация..." : "Активировать"}
                </button>
            </div>
        </form>
    );
}

// Info display component (non-editable)
function InfoField({ label, value }) {
    return (
        <div className="info-field">
            <span className="field-label">{label}</span>
            <div className="field-value">{value}</div>
        </div>
    );
}

// Toggle field component for boolean settings
function ToggleField({ 
    label, 
    description, 
    checked, 
    onChange, 
    disabled,
    error,
    onDismissError
}) {
    return (
        <div className="editable-field toggle-field">
            <div className="field-header">
                <span className="field-label">{label}</span>
            </div>
            <div className="toggle-field-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                <span className="field-description" style={{ fontSize: '0.9rem', color: 'var(--text-muted, #6c757d)' }}>
                    {description}
                </span>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '46px', height: '24px', flexShrink: 0 }}>
                    <input 
                        type="checkbox" 
                        checked={checked} 
                        onChange={(e) => onChange(e.target.checked)}
                        disabled={disabled}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span className="slider" style={{
                        position: 'absolute',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: checked ? 'var(--primary, #007bff)' : '#ccc',
                        transition: '.3s',
                        borderRadius: '24px'
                    }}>
                        <span className="slider-knob" style={{
                            position: 'absolute',
                            content: '""',
                            height: '18px',
                            width: '18px',
                            left: checked ? '24px' : '4px',
                            bottom: '3px',
                            backgroundColor: 'white',
                            transition: '.3s',
                            borderRadius: '50%'
                        }} />
                    </span>
                </label>
            </div>
            {error && <ErrorMessage message={error} onDismiss={onDismissError} />}
        </div>
    );
}

// Modal component for confirmations
function Modal({ isOpen, onClose, title, children }) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div 
                className="modal-content" 
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                    </svg>
                </button>
                
                <div className="modal-header">
                    <h2 id="modal-title" className="modal-title">{title}</h2>
                </div>
                
                <div className="modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

// Logout confirmation component
function LogoutConfirmation({ onConfirm, onCancel }) {
    return (
        <>
            <p className="logout-confirmation-text">
                Вы действительно хотите выйти из своего аккаунта?
            </p>
            <div className="logout-confirmation-actions">
                <button className="btn-logout-cancel" onClick={onCancel}>
                    Отмена
                </button>
                <button className="btn-logout-confirm" onClick={onConfirm}>
                    Да, уверен
                </button>
            </div>
        </>
    );
}

// Unlink confirmation component
function UnlinkConfirmation({ contactType, onConfirm, onCancel, isLoading, error }) {
    return (
        <>
            {error && (
                <div className="profile-error" style={{ marginBottom: '16px' }} role="alert">
                    <div className="profile-error-content">
                        <p className="profile-error-text">{error}</p>
                    </div>
                </div>
            )}
            <p className="logout-confirmation-text">
                Вы уверены, что хотите отвязать {contactType}?
            </p>
            <div className="logout-confirmation-actions">
                <button className="btn-logout-cancel" onClick={onCancel} disabled={isLoading}>
                    Отмена
                </button>
                <button className="btn-logout-confirm" onClick={onConfirm} disabled={isLoading}>
                    {isLoading ? "Отвязка..." : "Да, отвязать"}
                </button>
            </div>
        </>
    );
}

// Button to launch tutorial (only on desktop)
function TutorialButton({ isDoctor }) {
    const { startTutorial, isActive } = useTutorial();
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window === 'undefined') return true;
        return window.innerWidth > 768;
    });

    useEffect(() => {
        const checkIsDesktop = () => {
            setIsDesktop(window.innerWidth > 768);
        };

        checkIsDesktop();
        window.addEventListener('resize', checkIsDesktop);
        return () => window.removeEventListener('resize', checkIsDesktop);
    }, []);

    if (isActive || !isDesktop) return null;

    return (
        <button 
            className="tutorial-launch-btn"
            onClick={() => startTutorial(isDoctor)}
            type="button"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            Пройти обучение
        </button>
    );
}

export default function Profile() {
    const nav = useNavigate();
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [unlinkModal, setUnlinkModal] = useState({ isOpen: false, type: null, error: '', loading: false });
    const [doctorLoading, setDoctorLoading] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [toggleError, setToggleError] = useState("");

    const { data, error, loading, refetch } = useUser();
    const { logout: sessionLogout } = useContext(UserSessionContext);
    const { isActive: isTutorialActive } = useTutorial();

    // Redirect on auth error
    useEffect(() => {
        if (error) nav(ROUTES.AUTH, { viewTransition: true });
    }, [nav, error]);

    // Handle logout
    const handleLogout = async () => {
        await sessionLogout();
        nav(ROUTES.AUTH, { viewTransition: true });
    };

    // Save handlers
    const handleToggleEmailAlerts = async (checked) => {
        setToggleLoading(true);
        setToggleError("");
        try {
            const response = await putAuthorized("/client/update_email_alerts", {}, {
                params: { send_email_alerts: checked }
            });
            if (response.status >= 200 && response.status < 300) {
                if (refetch) refetch();
            } else {
                throw new Error(response.data?.error_description || "Не удалось обновить настройки уведомлений");
            }
        } catch (err) {
            const errorDesc = err.response?.data?.error_description 
                || err.message 
                || "Не удалось обновить настройки уведомлений";
            setToggleError(errorDesc);
        } finally {
            setToggleLoading(false);
        }
    };

    const handleSaveName = async (newName) => {
        const response = await putAuthorized("/client/update_full_name", {}, {
            params: { new_full_name: newName }
        });
        
        if (response.status >= 200 && response.status < 300) {
            if (refetch) refetch();
        } else {
            throw new Error("Не удалось обновить имя");
        }
    };

    const handleSaveEmail = async (newEmail) => {
        try {
            // Email requires confirmation via code
            const response = await getAuthorized("/client/send_reset_email_verification_code_on_email", {
                params: { new_email: newEmail }
            });
            
            if (response.status >= 200 && response.status < 300) {
                // Redirect to confirmation page
                nav(ROUTES.CONFIRM_CHANGE, {
                    state: { coderequired: ["email"] },
                    viewTransition: true
                });
            } else {
                // Если статус не 2xx, берем error_description из ответа
                const errorDesc = response.data?.error_description || "Не удалось отправить код подтверждения";
                throw new Error(errorDesc);
            }
        } catch (err) {
            // Если axios бросил исключение (4xx, 5xx), берем error_description из err.response
            const errorDesc = err.response?.data?.error_description 
                || err.message 
                || "Не удалось отправить код подтверждения";
            throw new Error(errorDesc);
        }
    };

    const handleActivateDoctor = async (code) => {
        setDoctorLoading(true);
        try {
            const response = await getAuthorized("/client/enable_doctor_features", {
                params: { access_key: code }
            });
            
            if (response.status >= 200 && response.status < 300) {
                if (refetch) refetch();
            } else {
                throw new Error("Неверный код доступа");
            }
        } catch (err) {
            throw new Error("Неверный код доступа");
        } finally {
            setDoctorLoading(false);
        }
    };

    // Validators
    const validateName = (value) => {
        if (!value || value.trim().length < 2) {
            return "Имя должно содержать минимум 2 символа";
        }
        if (value.trim().length > 100) {
            return "Имя слишком длинное";
        }
        return null;
    };

    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
            return "Введите корректный email";
        }
        return null;
    };

    const validatePhone = (value) => {
        const phoneRegex = /^\+\d{10,}$/;
        if (!value || !phoneRegex.test(value)) {
            return "Введите номер в формате +79991234567";
        }
        return null;
    };

    const handleRequestUnlink = (type) => {
        setUnlinkModal({ isOpen: true, type, error: '', loading: false });
    };

    const handleCloseUnlinkModal = () => {
        setUnlinkModal(prev => ({ ...prev, isOpen: false, error: '' }));
    };

    const handleConfirmUnlink = async () => {
        setUnlinkModal(prev => ({ ...prev, loading: true, error: '' }));

        try {
            const endpoint = unlinkModal.type === 'email'
                ? "/client/unlink_email"
                : "/client/unlink_phone_number";

            const response = await postAuthorized(endpoint, {});
            const data = response.data;

            if (data?.error_code) {
                throw new Error(data.error_description || data.message || "Ошибка");
            }

            if (refetch) refetch();
            setUnlinkModal({ isOpen: false, type: null, error: '', loading: false });
        } catch (err) {
            const errorDesc = err.response?.data?.error_description
                || err.message
                || "Не удалось выполнить отвязку";
            setUnlinkModal(prev => ({ ...prev, loading: false, error: errorDesc }));
        }
    };

    const handleSavePhone = async (newPhone) => {
        try {
            const response = await getAuthorized("/client/request_phone_number_change", {
                params: { new_phone_number: newPhone }
            });

            if (response.status >= 200 && response.status < 300) {
                const { auth_code, target_sms_number, message, sms_text } = response.data;
                nav(ROUTES.AUTH_SMS_VERIFY, {
                    state: {
                        phoneNumber: newPhone,
                        authCode: auth_code,
                        targetSmsNumber: target_sms_number,
                        message,
                        smsText: sms_text,
                        flow: "change"
                    },
                    viewTransition: true
                });
            } else {
                const errorDesc = response.data?.error_description || "Не удалось отправить запрос";
                throw new Error(errorDesc);
            }
        } catch (err) {
            const errorDesc = err.response?.data?.error_description
                || err.message
                || "Не удалось отправить запрос";
            throw new Error(errorDesc);
        }
    };

    const userIsDoctor = isUserDoctor(data);

    return (
        <>
        <SEO 
            title="Личный кабинет"
            description="Ваш персональный дашборд для управления здоровьем, просмотра результатов опросников и отслеживания показателей."
            keywords="личный кабинет, дашборд, здоровье, опросники"
            noindex={true}
        />
        <div>
            <ProfileNavigation isDoctor={userIsDoctor} />
            <div id="profile-handler">
                <div id="profile">
                    <div id="profile-header">
                        <h1>Профиль</h1>
                    </div>

                    <div className="profile-fields">
                        {/* Name Field */}
                        <EditableField
                            label="Полное имя"
                            currentValue={data?.full_name}
                            placeholder="Не указано"
                            onSave={handleSaveName}
                            validate={validateName}
                        />

                        {/* Email Field */}
                        <EditableField
                            label="Эл. почта"
                            currentValue={data?.email}
                            type="email"
                            onSave={handleSaveEmail}
                            onRequestUnlink={data?.phone_number ? () => handleRequestUnlink('email') : null}
                            validate={validateEmail}
                        />

                        {/* Phone Field */}
                        <EditableField
                            label="Телефон"
                            currentValue={data?.phone_number}
                            placeholder="Не указан"
                            onSave={handleSavePhone}
                            onRequestUnlink={data?.email ? () => handleRequestUnlink('phone') : null}
                            validate={validatePhone}
                        />

                        {/* Role Field */}
                        {userIsDoctor ? (
                            <>
                                <InfoField 
                                    label="Роль" 
                                    value="Врач 👨‍⚕️" 
                                />
                                <ToggleField
                                    label="Email-оповещения"
                                    description="Получать уведомления о критических рисках S3/S4 на привязанную почту"
                                    checked={data?.send_email_alerts || false}
                                    onChange={handleToggleEmailAlerts}
                                    disabled={toggleLoading}
                                    error={toggleError}
                                    onDismissError={() => setToggleError("")}
                                />
                                {hasRole(data, "admin") && <MedicalDocumentsSection isDoctor={true} />}
                            </>
                        ) : (
                            <div className="role-section">
                                <InfoField 
                                    label="Роль" 
                                    value="Пациент" 
                                />
                                <DoctorActivation 
                                    onActivate={handleActivateDoctor}
                                    isLoading={doctorLoading}
                                />
                                {hasRole(data, "admin") && <MedicalDocumentsSection isDoctor={false} />}
                            </div>
                        )}
                    </div>

                    <Modal 
                        isOpen={showLogoutConfirm}
                        onClose={() => setShowLogoutConfirm(false)}
                        title="Вы уверены, что хотите выйти?"
                    >
                        <LogoutConfirmation 
                            onConfirm={handleLogout}
                            onCancel={() => setShowLogoutConfirm(false)}
                        />
                    </Modal>

                    <Modal 
                        isOpen={unlinkModal.isOpen}
                        onClose={handleCloseUnlinkModal}
                        title={`Отвязать ${unlinkModal.type === 'email' ? 'почту' : 'телефон'}?`}
                    >
                        <UnlinkConfirmation 
                            contactType={unlinkModal.type === 'email' ? 'почту' : 'телефон'}
                            onConfirm={handleConfirmUnlink}
                            onCancel={handleCloseUnlinkModal}
                            isLoading={unlinkModal.loading}
                            error={unlinkModal.error}
                        />
                    </Modal>

                    <div className="profile-footer">
                        {!isTutorialActive && (
                            <TutorialButton isDoctor={userIsDoctor} />
                        )}
                        
                        <button 
                            className="profile-logout-btn"
                            onClick={() => setShowLogoutConfirm(true)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                <polyline points="16 17 21 12 16 7"></polyline>
                                <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            Выйти из аккаунта
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
